import type { AgentStreamPayload } from './nlp.js'
import { nlpApi } from './nlp.js'

/** Minimal shape of the handle `agentStream.initiate` dispatches back. */
interface TurnSubscription {
  unsubscribe: () => void
}

type TurnDispatch = (thunk: unknown) => TurnSubscription

/**
 * The live `agentStream` subscription per agent. At most one entry per agent
 * is retained, so retained cache memory is O(agents) rather than O(turns).
 */
const liveTurns = new Map<string, TurnSubscription>()

/**
 * Start an agent turn and take ownership of its RTK Query subscription.
 *
 * Why this exists: every call site dispatches `agentStream.initiate` as
 * fire-and-forget, because the turn's lifetime must be owned by the store
 * rather than by whatever component happens to be mounted — closing the
 * popup must not abort the turn, and the entry has to outlive the UI for the
 * live→persisted reconciliation in `AgentChat`. But `initiate()` subscribes by
 * default, and `agentStream` sets `keepUnusedDataFor: 0`, so an entry is only
 * evicted when its last subscriber detaches. Never unsubscribing therefore
 * pinned EVERY turn's entry — accumulated text plus the full `parts` array of
 * every stream chunk — for the whole page session, and made
 * `useAgentTurnStream` (which scans all `agentStream` entries on every render
 * of every consumer) grow linearly with turns taken.
 *
 * Releasing the PREVIOUS turn for the same agent when a new one starts is safe
 * by construction: `useAgentTurnStream` already selects only the entry with the
 * greatest `startedTimeStamp` per agent, so any older entry is unreachable
 * dead weight. The newest turn per agent stays subscribed for exactly as long
 * as reconciliation needs it.
 */
export const startAgentTurn = (dispatch: TurnDispatch, payload: AgentStreamPayload): void => {
  const previous = liveTurns.get(payload.agentId)
  const subscription = dispatch(nlpApi.endpoints.agentStream.initiate(payload))
  liveTurns.set(payload.agentId, subscription)
  // Release AFTER the new subscription is registered — dropping the last
  // subscriber first would let RTK Query evict the entry between the two
  // calls, discarding a reconciliation still in flight.
  previous?.unsubscribe()
}
